import { useMemo, useRef, useState } from 'react';
import { UnauthorizedViewerRequestError } from '../services/adminApiClient';
import {
  createViewerPreviewRelease,
  deployViewerAuthoringProject,
  fetchViewerAuthoringJobStatus,
} from '../Viewer/authoring/api/viewerAuthoringApi';
import { resolveViewerDeploymentActionState } from '../Viewer/authoring/mappers/viewerDeploymentActionState';
import type { ViewerDraftSaveStatus, ViewerJobStatus } from '../types/viewerDeployment';

interface UseViewerDeploymentActionsParams {
  enabled: boolean;
  companyId: string | null;
  productId: string | null;
  projectStatus: {
    hasLiveDeployment: boolean;
    currentPreviewReleaseId: string | null;
  } | null;
  expectedDraftRevision?: number | null;
  saveStatus?: ViewerDraftSaveStatus;
  onProjectStatusChanged?: () => Promise<void> | void;
}

function buildIdempotencyKey(prefix: 'preview' | 'deploy'): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}`;
}

export function useViewerDeploymentActions(params: UseViewerDeploymentActionsParams) {
  const {
    enabled,
    companyId,
    productId,
    projectStatus,
    expectedDraftRevision = null,
    saveStatus = 'idle',
    onProjectStatusChanged,
  } = params;
  const [isPreviewPending, setIsPreviewPending] = useState(false);
  const [isDeployPending, setIsDeployPending] = useState(false);
  const [jobStatus, setJobStatus] = useState<ViewerJobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimeoutRef = useRef<number | null>(null);

  const clearJobPoll = () => {
    if (pollTimeoutRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  };

  const hasActiveDeployJob = jobStatus?.status === 'queued' || jobStatus?.status === 'running';
  const actionState = useMemo(
    () =>
      resolveViewerDeploymentActionState({
        enabled,
        companyId,
        productId,
        hasLiveDeployment: Boolean(projectStatus?.hasLiveDeployment),
        isPreviewPending,
        isDeployPending,
        hasActiveDeployJob,
        hasCurrentPreviewRelease: Boolean(projectStatus?.currentPreviewReleaseId),
        expectedDraftRevision,
        saveStatus,
      }),
    [
      companyId,
      enabled,
      expectedDraftRevision,
      hasActiveDeployJob,
      isDeployPending,
      isPreviewPending,
      productId,
      projectStatus?.currentPreviewReleaseId,
      projectStatus?.hasLiveDeployment,
      saveStatus,
    ],
  );
  const previewLabel = actionState.previewLabel;
  const canCreatePreview = actionState.canCreatePreview;
  const canDeploy = actionState.canDeploy;

  const createPreview = async () => {
    if (!canCreatePreview) {
      setError(actionState.previewBlockedMessage);
      return null;
    }

    setIsPreviewPending(true);
    setError(null);

    try {
      const payload = await createViewerPreviewRelease(
        companyId!,
        productId!,
        buildIdempotencyKey('preview'),
        expectedDraftRevision ?? undefined,
      );
      await onProjectStatusChanged?.();
      return payload;
    } catch (nextError) {
      setError(
        nextError instanceof UnauthorizedViewerRequestError
          ? 'Session expired. Please sign in again.'
          : nextError instanceof Error
            ? nextError.message
            : 'Failed to create preview.',
      );
      return null;
    } finally {
      setIsPreviewPending(false);
    }
  };

  const pollJobUntilTerminal = async (jobId: string): Promise<ViewerJobStatus | null> => {
    if (!companyId || !productId) return null;

    const nextStatus = await fetchViewerAuthoringJobStatus(companyId, productId, jobId);
    setJobStatus(nextStatus);

    if (nextStatus.status === 'queued' || nextStatus.status === 'running') {
      if (typeof window !== 'undefined') {
        await new Promise<void>((resolve) => {
          pollTimeoutRef.current = window.setTimeout(resolve, 400);
        });
      }

      return pollJobUntilTerminal(jobId);
    }

    return nextStatus;
  };

  const deploy = async () => {
    if (!canDeploy) {
      setError(actionState.deployBlockedMessage);
      return null;
    }

    setIsDeployPending(true);
    setError(null);
    clearJobPoll();

    try {
      const payload = await deployViewerAuthoringProject(
        companyId!,
        productId!,
        buildIdempotencyKey('deploy'),
        projectStatus?.currentPreviewReleaseId ?? undefined,
      );
      const jobId = typeof payload.jobId === 'string' ? payload.jobId : null;
      if (jobId) {
        await pollJobUntilTerminal(jobId);
      }
      await onProjectStatusChanged?.();
      return payload;
    } catch (nextError) {
      setError(
        nextError instanceof UnauthorizedViewerRequestError
          ? 'Session expired. Please sign in again.'
          : nextError instanceof Error
            ? nextError.message
            : 'Failed to deploy viewer project.',
      );
      return null;
    } finally {
      clearJobPoll();
      setIsDeployPending(false);
    }
  };

  return {
    previewLabel,
    isPreviewPending,
    isDeployPending,
    canCreatePreview,
    canDeploy,
    deploymentError: error,
    jobStatus,
    createPreview,
    deploy,
  };
}
